import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Users } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getAccountIconComponent } from "@/components/social-icons";
import { Slider } from "@/components/ui/slider";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import { useI18n } from "@/lib/i18n";
import { type AccountProduct } from "@/lib/mock-data";
import { SITE_URL } from "@/lib/site-url";
import * as LucideIcons from "lucide-react";

export const Route = createFileRoute("/_app/accounts")({
  component: AccountsPage,
  head: () => ({
    meta: [
      { title: "Buy Pre-made Accounts — SmsEmpire" },
      {
        name: "description",
        content:
          "Verified social media, email and marketplace accounts delivered instantly. Choose custom followers for Instagram, X and Facebook. Pay-as-you-go, no subscription.",
      },
      { property: "og:title", content: "Buy Pre-made Accounts — SmsEmpire" },
      {
        property: "og:description",
        content: "Ready-to-use accounts with custom followers, delivered instantly.",
      },
      { property: "og:url", content: `${SITE_URL}/accounts` },
      { property: "og:image", content: `${SITE_URL}/og/default.png` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/accounts` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Pre-made Verified Accounts",
          description: "Ready-to-use social media, email and marketplace accounts.",
          url: `${SITE_URL}/accounts`,
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              item: {
                "@type": "Product",
                name: "Instagram — Aged Account",
                description: "Verified email, 30+ days old, ready for use.",
                url: `${SITE_URL}/accounts`,
                offers: {
                  "@type": "Offer",
                  priceCurrency: "USD",
                  price: "3.50",
                  availability: "https://schema.org/InStock",
                },
              },
            },
            {
              "@type": "ListItem",
              position: 2,
              item: {
                "@type": "Product",
                name: "X (Twitter) Account",
                description: "Phone + email verified, blank profile.",
                url: `${SITE_URL}/accounts`,
                offers: {
                  "@type": "Offer",
                  priceCurrency: "USD",
                  price: "4.20",
                  availability: "https://schema.org/InStock",
                },
              },
            },
            {
              "@type": "ListItem",
              position: 3,
              item: {
                "@type": "Product",
                name: "Facebook — BM Ready",
                description: "Ready for Business Manager access.",
                url: `${SITE_URL}/accounts`,
                offers: {
                  "@type": "Offer",
                  priceCurrency: "USD",
                  price: "6.90",
                  availability: "https://schema.org/InStock",
                },
              },
            },
            {
              "@type": "ListItem",
              position: 4,
              item: {
                "@type": "Product",
                name: "Gmail — Fresh",
                description: "New Gmail with recovery email.",
                url: `${SITE_URL}/accounts`,
                offers: {
                  "@type": "Offer",
                  priceCurrency: "USD",
                  price: "1.50",
                  availability: "https://schema.org/InStock",
                },
              },
            },
            {
              "@type": "ListItem",
              position: 5,
              item: {
                "@type": "Product",
                name: "Discord — Warmed",
                description: "7+ days old, joined servers.",
                url: `${SITE_URL}/accounts`,
                offers: {
                  "@type": "Offer",
                  priceCurrency: "USD",
                  price: "2.10",
                  availability: "https://schema.org/InStock",
                },
              },
            },
            {
              "@type": "ListItem",
              position: 6,
              item: {
                "@type": "Product",
                name: "Shopify Buyer",
                description: "Trusted buyer profile with history.",
                url: `${SITE_URL}/accounts`,
                offers: {
                  "@type": "Offer",
                  priceCurrency: "USD",
                  price: "8.00",
                  availability: "https://schema.org/InStock",
                },
              },
            },
          ],
        }),
      },
    ],
  }),
});

// $0.008 per follower
const PER_FOLLOWER = 0.008;
const FOLLOWER_STEPS = [0, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000];

function AccountsPage() {
  const { t } = useI18n();
  const { format } = useCurrency();
  const { deductBalance } = useAuth();
  const [picked, setPicked] = useState<any | null>(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [accounts, setAccounts] = useState<any[]>([]);

  useEffect(() => {
    api
      .get<any[]>("/api/accounts/products")
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setAccounts(
            data.map((d) => {
              const IconComponent = getAccountIconComponent(d.icon_name);
              return {
                id: d.code,
                name: d.name,
                description: d.description,
                priceUsd: d.price_usd,
                stock: d.stock,
                icon: IconComponent,
                color: d.color,
                followers: d.has_followers,
              };
            }),
          );
        }
      })
      // Was `.catch(() => {})`, which swallowed the ReferenceError thrown inside the
      // .then above (getAccountIconComponent was never imported) and left the catalogue
      // permanently empty with no sign anything had failed.
      .catch((error) => {
        console.error("Failed to load account products", error);
        toast.error(t("accounts.load_failed"));
      });
  }, [t]);

  const navigate = useNavigate();

  async function buyDirect(a: AccountProduct) {
    try {
      await api.post("/api/purchases/accounts", { account_id: a.id, followers: 0 });
      toast.success(t("accounts.order_placed"));
      window.dispatchEvent(new Event("balance_updated"));
      navigate({ to: "/activations" });
    } catch (error) {
      // Surface the backend's reason (usually insufficient balance) instead of a
      // blanket "server error" that told the user nothing.
      toast.error(error instanceof ApiError ? error.message : t("common.error_server"));
    }
  }

  function openPicker(a: AccountProduct) {
    setPicked(a);
    setStepIdx(0);
  }

  async function confirmBuy() {
    if (!picked) return;
    const followers = FOLLOWER_STEPS[stepIdx];
    try {
      await api.post("/api/purchases/accounts", { account_id: picked.id, followers });
      toast.success(t("accounts.order_placed"));
      setPicked(null);
      window.dispatchEvent(new Event("balance_updated"));
      navigate({ to: "/activations" });
    } catch (error) {
      // Surface the backend's reason (usually insufficient balance) instead of a
      // blanket "server error" that told the user nothing.
      toast.error(error instanceof ApiError ? error.message : t("common.error_server"));
    }
  }

  const followersSel = picked ? FOLLOWER_STEPS[stepIdx] : 0;
  const followersCost = followersSel * PER_FOLLOWER;
  const totalCost = picked ? picked.priceUsd + followersCost : 0;

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("accounts.title")}</h1>
        <p className="mt-1 text-muted-foreground">{t("accounts.subtitle")}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {accounts.map((a) => {
          const Icon = a.icon;
          return (
            <Card
              key={a.id}
              className="group flex flex-col overflow-hidden border-border bg-card p-5 transition-all hover:border-primary/40 hover:shadow-glow"
            >
              <div className="flex items-start justify-between">
                <div
                  className="flex h-11 w-11 items-center justify-center rounded-xl"
                  style={{ background: `${a.color}22`, color: a.color }}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <span className="rounded-full bg-success/15 px-2 py-0.5 text-xs font-medium text-success">
                  {t("accounts.stock")}: {a.stock}
                </span>
              </div>
              <h3 className="mt-4 text-lg font-semibold">{a.name}</h3>
              <p className="mt-1 flex-1 text-sm text-muted-foreground">{a.description}</p>
              {a.followers && (
                <p className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary">
                  <Users className="h-3 w-3" /> {t("accounts.followers")}
                </p>
              )}
              <div className="mt-4 flex items-center justify-between">
                <p className="text-2xl font-bold">{format(a.priceUsd)}</p>
                <Button
                  size="sm"
                  className="bg-gradient-primary"
                  onClick={() => (a.followers ? openPicker(a) : buyDirect(a))}
                >
                  {t("accounts.buy")}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!picked} onOpenChange={(o) => !o && setPicked(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{picked?.name}</DialogTitle>
            <DialogDescription>{t("accounts.followers_desc")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            <div>
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium">{t("accounts.followers")}</span>
                <span className="text-lg font-bold">{followersSel.toLocaleString()}</span>
              </div>
              <Slider
                min={0}
                max={FOLLOWER_STEPS.length - 1}
                step={1}
                value={[stepIdx]}
                onValueChange={(v) => setStepIdx(v[0])}
              />
              <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                <span>0</span>
                <span>100k</span>
              </div>
            </div>

            <div className="space-y-2 rounded-xl border border-border bg-muted/40 p-4 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("accounts.base_price")}</span>
                <span>{picked && format(picked.priceUsd)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("accounts.followers_price")}</span>
                <span>{format(followersCost)}</span>
              </div>
              <div className="mt-1 flex justify-between border-t border-border pt-2 text-base font-semibold">
                <span>{t("accounts.total")}</span>
                <span>{format(totalCost)}</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setPicked(null)}>
              {t("common.cancel")}
            </Button>
            <Button className="bg-gradient-primary" onClick={confirmBuy}>
              {t("accounts.confirm_buy")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
